package com.castly.admin.ui

import androidx.compose.runtime.*
import com.castly.admin.api.HomeSectionApiClient
import com.castly.admin.external.Sortable
import com.castly.admin.external.SortableInstance
import com.castly.admin.external.createSortableOptions
import com.castly.admin.ui.components.ConfirmDialog
import com.castly.admin.ui.components.IconButton
import com.castly.admin.ui.icons.IconEdit
import com.castly.admin.ui.icons.IconRefreshCw
import com.castly.admin.ui.icons.IconTrash
import com.castly.admin.ui.theme.AppTheme
import com.castly.admin.ui.theme.BorderRadius
import com.castly.admin.ui.theme.Spacing
import com.castly.admin.ui.theme.Typography
import com.castly.admin.ui.theme.themeTransition
import com.castly.shared.dto.AdminSectionDto
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.HTMLElement
import kotlin.coroutines.resume
import kotlin.coroutines.suspendCoroutine

/**
 * Home section management UI (Simplified for Phase 4)
 *
 * Features:
 * - List sections
 * - Create basic sections
 * - Edit sections
 * - Delete sections
 * - View archived sections
 *
 * Note: Full drag-and-drop and advanced features will be implemented in later phases
 */
@Composable
fun HomeSection(apiClient: HomeSectionApiClient) {
    val colors = AppTheme.colors
    var sections by remember { mutableStateOf<List<AdminSectionDto>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    // T045: "Show Deleted" toggle - when true, loads all sections (active + deleted)
    var showDeleted by remember { mutableStateOf(false) }
    var showEditorModal by remember { mutableStateOf(false) }
    var editingSectionId by remember { mutableStateOf<String?>(null) }
    var showAddMenu by remember { mutableStateOf(false) }
    var selectedSectionType by remember { mutableStateOf<String?>(null) }
    // T057: SortableJS container ref
    var listContainer by remember { mutableStateOf<HTMLElement?>(null) }
    // T063: Toast message for reorder conflicts/errors
    var toastMessage by remember { mutableStateOf<String?>(null) }
    // T030: ConfirmDialog state for delete confirmation
    var confirmDeleteId by remember { mutableStateOf<String?>(null) }

    val scope = rememberCoroutineScope()

    // Auto-dismiss toast after 3 seconds
    LaunchedEffect(toastMessage) {
        if (toastMessage != null) {
            delay(3000)
            toastMessage = null
        }
    }

    // Load sections on mount; reload when showDeleted toggle changes
    LaunchedEffect(showDeleted) {
        loading = true
        error = null
        try {
            // T044: Use includeDeleted=true when "Show Deleted" is toggled on
            sections = apiClient.getAllSections(includeDeleted = showDeleted)
        } catch (e: Exception) {
            error = e.message ?: "Failed to load sections"
            console.error("Error loading sections", e)
        } finally {
            loading = false
        }
    }

    // Delete section
    fun handleDelete(sectionId: String) {
        scope.launch {
            try {
                apiClient.deleteSection(sectionId)
                // Reload to reflect updated state (deleted section may appear if showDeleted=true)
                sections = apiClient.getAllSections(includeDeleted = showDeleted)
            } catch (e: Exception) {
                error = "Failed to delete section: ${e.message}"
                console.error("Error deleting section", e)
            }
        }
    }

    // Restore section
    fun handleRestore(sectionId: String) {
        scope.launch {
            try {
                apiClient.restoreSection(sectionId)
                // Reload to reflect restored section
                sections = apiClient.getAllSections(includeDeleted = showDeleted)
            } catch (e: Exception) {
                error = "Failed to restore section: ${e.message}"
                console.error("Error restoring section", e)
            }
        }
    }

    // T059/T060/T062: Reorder sections with auto-save
    // NOTE: Do NOT update Compose state here — SortableJS already moved the DOM elements.
    // Updating state would trigger a recomposition that conflicts with SortableJS's DOM changes.
    fun handleReorder(newOrder: List<String>) {
        scope.launch {
            try {
                apiClient.reorderSections(newOrder)
            } catch (e: Exception) {
                // Revert to server state if API call fails
                try {
                    sections = apiClient.getAllSections(includeDeleted = showDeleted)
                } catch (reloadEx: Exception) {
                    error = "Failed to reload sections: ${reloadEx.message}"
                }
                toastMessage = "Failed to save order. Reverted to server state."
                console.error("Error reordering sections", e)
            }
        }
    }

    // T057/T058: SortableJS integration for drag-and-drop reordering
    DisposableEffect(listContainer, showDeleted) {
        var sortableInstance: SortableInstance? = null
        val container = listContainer

        if (container != null && !showDeleted) {
            val options = createSortableOptions {
                animation = 150
                delay = 100
                delayOnTouchOnly = true
                touchStartThreshold = 10
                // T058: Drag only via the handle; ghost class for drag feedback
                handle = ".drag-handle"
                ghostClass = "sortable-ghost"
                dragClass = "sortable-drag"

                // T059: Extract new order from DOM data-section-id attributes on drop
                onEnd = { _ ->
                    val elements = container.querySelectorAll(".home-section-item")
                    val newOrder = (0 until elements.length).mapNotNull { i ->
                        (elements.item(i) as? org.w3c.dom.Element)?.getAttribute("data-section-id")
                    }
                    if (newOrder.isNotEmpty()) {
                        handleReorder(newOrder)
                    }
                }
            }
            sortableInstance = Sortable.create(container, options)
        }

        onDispose { sortableInstance?.destroy() }
    }

    // Function to upload banner image and wait for S3 URL
    suspend fun uploadBannerImageAndGetUrl(file: org.w3c.files.File, sectionId: String, language: String): String? {
        return suspendCoroutine { continuation ->
            try {
                val formData = org.w3c.xhr.FormData()
                formData.append("file", file)
                formData.append("language", language)

                val token = kotlinx.browser.window.localStorage.getItem("id_token")
                val xhr = org.w3c.xhr.XMLHttpRequest()

                xhr.addEventListener("load", {
                    if (xhr.status == 200.toShort()) {
                        try {
                            val response = JSON.parse<dynamic>(xhr.responseText)
                            val imageUrl = response.key as? String
                            console.log("Banner uploaded successfully: $imageUrl")
                            continuation.resume(imageUrl)
                        } catch (e: Exception) {
                            console.error("Failed to parse upload response", e)
                            console.error("Response text: ${xhr.responseText}")
                            continuation.resume(null)
                        }
                    } else {
                        console.error("Upload failed with status ${xhr.status}: ${xhr.responseText}")
                        continuation.resume(null)
                    }
                })

                xhr.addEventListener("error", {
                    console.error("Upload network error")
                    continuation.resume(null)
                })

                xhr.open("POST", "${apiClient.baseUrl}/api/admin/sections/$sectionId/upload-banner")
                if (!token.isNullOrBlank()) {
                    xhr.setRequestHeader("Authorization", "Bearer $token")
                }
                xhr.send(formData)
            } catch (e: Exception) {
                console.error("Upload exception", e)
                continuation.resume(null)
            }
        }
    }

    Div({
        style {
            backgroundColor(Color(colors.surface))
            borderRadius(BorderRadius.lg)
            padding(Spacing.xxl)
            border(1.px, LineStyle.Solid, Color(colors.border))
            themeTransition()
        }
    }) {
        // Header
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
                marginBottom(Spacing.xxl)
            }
        }) {
            H2({ style { margin(0.px) } }) {
                Text("Home Page Sections")
            }

            Div({
                style {
                    display(DisplayStyle.Flex)
                    property("gap", "8px")
                    alignItems(AlignItems.Center)
                    property("position", "relative")
                }
            }) {
                // Add Section button with dropdown
                Div({
                        style {
                            property("position", "relative")
                        }
                    }) {
                        com.castly.admin.ui.components.Tooltip(text = "Add Section") {
                            Button({
                                onClick {
                                    showAddMenu = !showAddMenu
                                }
                                attr("aria-label", "Add Section")
                                style {
                                    padding(Spacing.sm)
                                    backgroundColor(Color(colors.primary))
                                    color(Color("#ffffff"))
                                    border(0.px)
                                    borderRadius(BorderRadius.md)
                                    cursor("pointer")
                                    display(DisplayStyle.Flex)
                                    alignItems(AlignItems.Center)
                                    justifyContent(JustifyContent.Center)
                                    width(36.px)
                                    height(36.px)
                                }
                            }) {
                                com.castly.admin.ui.icons.IconPlus(size = 18)
                            }
                        }

                        // Dropdown menu
                        if (showAddMenu) {
                            Div({
                                style {
                                    property("position", "absolute")
                                    property("top", "100%")
                                    property("left", "0")
                                    property("z-index", "1000")
                                    marginTop(Spacing.xs)
                                    backgroundColor(Color(colors.surface))
                                    border(1.px, LineStyle.Solid, Color(colors.border))
                                    borderRadius(BorderRadius.sm)
                                    property("box-shadow", "0 4px 6px rgba(0, 0, 0, 0.1)")
                                    minWidth(180.px)
                                    themeTransition()
                                }
                            }) {
                                // Banner option
                                Div({
                                    onClick {
                                        selectedSectionType = "banner"
                                        showAddMenu = false
                                        editingSectionId = null
                                        showEditorModal = true
                                    }
                                    style {
                                        padding(Spacing.md, Spacing.lg)
                                        cursor("pointer")
                                        border(1.px, LineStyle.Solid, Color(colors.border))
                                        property("transition", "background-color 0.2s")
                                    }
                                }) {
                                    Div({ style { fontWeight("bold"); marginBottom(Spacing.xs) } }) {
                                        Text("Banner")
                                    }
                                    Div({ style { fontSize(Typography.sm); color(Color(colors.textMuted)) } }) {
                                        Text("Promotional image")
                                    }
                                }

                                // Carousel option
                                Div({
                                    onClick {
                                        selectedSectionType = "carousel"
                                        showAddMenu = false
                                        editingSectionId = null
                                        showEditorModal = true
                                    }
                                    style {
                                        padding(Spacing.md, Spacing.lg)
                                        cursor("pointer")
                                        border(1.px, LineStyle.Solid, Color(colors.border))
                                        property("transition", "background-color 0.2s")
                                    }
                                }) {
                                    Div({ style { fontWeight("bold"); marginBottom(Spacing.xs) } }) {
                                        Text("Carousel")
                                    }
                                    Div({ style { fontSize(Typography.sm); color(Color(colors.textMuted)) } }) {
                                        Text("Scrolling content list")
                                    }
                                }

                                // Episode Carousel option
                                Div({
                                    onClick {
                                        selectedSectionType = "episode_carousel"
                                        showAddMenu = false
                                        editingSectionId = null
                                        showEditorModal = true
                                    }
                                    style {
                                        padding(Spacing.md, Spacing.lg)
                                        cursor("pointer")
                                        border(1.px, LineStyle.Solid, Color(colors.border))
                                        property("transition", "background-color 0.2s")
                                    }
                                }) {
                                    Div({ style { fontWeight("bold"); marginBottom(Spacing.xs) } }) {
                                        Text("Episode Carousel")
                                    }
                                    Div({ style { fontSize(Typography.sm); color(Color(colors.textMuted)) } }) {
                                        Text("Scrolling list of hand-picked episodes")
                                    }
                                }

                                // Static option
                                Div({
                                    onClick {
                                        selectedSectionType = "static"
                                        showAddMenu = false
                                        editingSectionId = null
                                        showEditorModal = true
                                    }
                                    style {
                                        padding(Spacing.md, Spacing.lg)
                                        cursor("pointer")
                                        border(1.px, LineStyle.Solid, Color(colors.border))
                                        property("transition", "background-color 0.2s")
                                    }
                                }) {
                                    Div({ style { fontWeight("bold"); marginBottom(Spacing.xs) } }) {
                                        Text("Static")
                                    }
                                    Div({ style { fontSize(Typography.sm); color(Color(colors.textMuted)) } }) {
                                        Text("Vendor placeholder")
                                    }
                                }

                                // Continue Watching option
                                Div({
                                    onClick {
                                        selectedSectionType = "continue_watching"
                                        showAddMenu = false
                                        editingSectionId = null
                                        showEditorModal = true
                                    }
                                    style {
                                        padding(Spacing.md, Spacing.lg)
                                        cursor("pointer")
                                        border(1.px, LineStyle.Solid, Color(colors.border))
                                        property("transition", "background-color 0.2s")
                                    }
                                }) {
                                    Div({ style { fontWeight("bold"); marginBottom(Spacing.xs) } }) {
                                        Text("Continue Watching")
                                    }
                                    Div({ style { fontSize(Typography.sm); color(Color(colors.textMuted)) } }) {
                                        Text("Single hero card (per user)")
                                    }
                                }

                                // Continue Watching Carousel option
                                Div({
                                    onClick {
                                        selectedSectionType = "continue_watching_carousel"
                                        showAddMenu = false
                                        editingSectionId = null
                                        showEditorModal = true
                                    }
                                    style {
                                        padding(Spacing.md, Spacing.lg)
                                        cursor("pointer")
                                        property("transition", "background-color 0.2s")
                                    }
                                }) {
                                    Div({ style { fontWeight("bold"); marginBottom(Spacing.xs) } }) {
                                        Text("Continue Watching Carousel")
                                    }
                                    Div({ style { fontSize(Typography.sm); color(Color(colors.textMuted)) } }) {
                                        Text("Scrolling progress list (per user)")
                                    }
                                }
                            }
                        }
                    }

                // T045: "Show Deleted" toggle checkbox
                Div({
                    onClick { showDeleted = !showDeleted }
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        property("gap", "6px")
                        cursor("pointer")
                        fontSize(Typography.base)
                        color(Color(colors.textMuted))
                    }
                }) {
                    Input(org.jetbrains.compose.web.attributes.InputType.Checkbox) {
                        if (showDeleted) attr("checked", "")
                        style {
                            cursor("pointer")
                            property("pointer-events", "none")
                        }
                    }
                    Text("Show Deleted")
                }
            }
        }

        // Error message
        error?.let { msg ->
            Div({
                style {
                    padding(Spacing.md)
                    backgroundColor(Color(if (colors == com.castly.admin.ui.theme.LightColors) "#fee2e2" else "#450a0a"))
                    color(Color(colors.danger))
                    borderRadius(BorderRadius.sm)
                    marginBottom(Spacing.lg)
                    themeTransition()
                }
            }) {
                Text(msg)
            }
        }

        // T063: Toast notification for reorder conflicts
        toastMessage?.let { msg ->
            Div({
                style {
                    padding(Spacing.md)
                    backgroundColor(Color(if (colors == com.castly.admin.ui.theme.LightColors) "#fff3cd" else "#451a03"))
                    color(Color(colors.warning))
                    borderRadius(BorderRadius.sm)
                    marginBottom(Spacing.lg)
                    border(1.px, LineStyle.Solid, Color(colors.warning))
                    themeTransition()
                }
            }) {
                Text(msg)
            }
        }

        // Loading state
        if (loading) {
            Div({ style { textAlign("center"); padding(40.px) } }) {
                Text("Loading sections...")
            }
        } else {
            // Section list
            if (sections.isEmpty()) {
                Div({
                    style {
                        textAlign("center")
                        padding(40.px)
                        color(Color(colors.textMuted))
                    }
                }) {
                    Text(if (showDeleted) "No sections found" else "No active sections")
                }
            } else {
                Div({
                    // T057: Capture container ref for SortableJS
                    ref { element ->
                        listContainer = element
                        onDispose { listContainer = null }
                    }
                    style {
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        property("gap", "12px")
                    }
                }) {
                    sections.forEach { section ->
                        SectionListItem(
                            section = section,
                            // T046: Determine archived status per-section, not globally
                            isArchived = section.deletedAt != null,
                            onEdit = {
                                editingSectionId = section.id
                                showEditorModal = true
                            },
                            onDelete = { confirmDeleteId = section.id },
                            onRestore = { handleRestore(section.id) }
                        )
                    }
                }
            }
        }
    }

    // Modal for section editor
    Modal(
        title = if (editingSectionId == null) "Create ${selectedSectionType?.let { type ->
            when (type) {
                "continue_watching" -> "Continue Watching"
                "continue_watching_carousel" -> "Continue Watching Carousel"
                "episode_carousel" -> "Episode Carousel"
                else -> type.replaceFirstChar { it.uppercase() }
            }
        } ?: ""} Section"
                else "Edit Section",
        isOpen = showEditorModal,
        onClose = {
            showEditorModal = false
            editingSectionId = null
            selectedSectionType = null
        },
        maxWidth = 900.px
    ) {
        // Determine correct baseUrl based on environment
        val baseUrl = remember {
            if (kotlinx.browser.window.location.hostname == "localhost") {
                "http://localhost:8080"
            } else {
                "https://api-${kotlinx.browser.window.location.hostname}"
            }
        }

        com.castly.admin.ui.home.HomeSectionEditorPage(
            sectionId = editingSectionId,
            initialSectionType = selectedSectionType,
            apiClient = com.castly.admin.api.ApiClient(baseUrl),
            homeSectionApiClient = apiClient,
            onBannerFilesChanged = { _, _ ->
                // Files are now handled within HomeSectionEditorPage
            },
            onUploadBannerImage = { file, sectionId, language ->
                uploadBannerImageAndGetUrl(file, sectionId, language)
            },
            onSaveComplete = { _ ->
                scope.launch {
                    // Close modal
                    showEditorModal = false
                    editingSectionId = null
                    selectedSectionType = null

                    // Reload sections
                    loading = true
                    try {
                        sections = apiClient.getAllSections(includeDeleted = showDeleted)
                    } catch (e: Exception) {
                        error = e.message ?: "Failed to reload sections"
                    } finally {
                        loading = false
                    }
                }
            },
            onBack = {
                showEditorModal = false
                editingSectionId = null
                selectedSectionType = null
            }
        )
    }

    // T030: ConfirmDialog for section deletion
    ConfirmDialog(
        isOpen = confirmDeleteId != null,
        title = "Delete Section",
        message = "Are you sure you want to delete this section?",
        confirmLabel = "Delete",
        onConfirm = {
            confirmDeleteId?.let { id -> handleDelete(id) }
            confirmDeleteId = null
        },
        onCancel = { confirmDeleteId = null }
    )
}

/**
 * T047: Format ISO 8601 deletion date as "YYYY-MM-DD HH:MM"
 */
private fun formatDeletionDate(isoString: String): String {
    // Input: "2026-02-20T12:30:45.123456Z" → Output: "2026-02-20 12:30"
    return if (isoString.length >= 16) {
        isoString.substring(0, 10) + " " + isoString.substring(11, 16)
    } else {
        isoString
    }
}

/**
 * Individual section list item
 * T048: Deleted sections shown with gray background and strikethrough name
 */
@Composable
fun SectionListItem(
    section: AdminSectionDto,
    isArchived: Boolean,
    onEdit: () -> Unit = {},
    onDelete: () -> Unit,
    onRestore: () -> Unit
) {
    val colors = AppTheme.colors
    Div({
        // T059: data-section-id for SortableJS order extraction
        attr("data-section-id", section.id)
        // T057: CSS class for SortableJS querySelector
        classes("home-section-item")
        style {
            padding(Spacing.lg)
            // T048: Gray background for deleted sections
            backgroundColor(if (isArchived) Color(colors.surfaceHover) else Color(colors.surface))
            border(1.px, LineStyle.Solid, Color(colors.border))
            borderRadius(BorderRadius.sm)
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.SpaceBetween)
            alignItems(AlignItems.Center)
            themeTransition()
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                property("gap", "12px")
            }
        }) {
            // T061: Drag handle icon for non-archived sections
            if (!isArchived) {
                Span({
                    classes("drag-handle")
                    style {
                        cursor("grab")
                        padding(Spacing.xs)
                        color(Color(colors.textMuted))
                        fontSize(Typography.xl)
                        property("user-select", "none")
                        property("line-height", "1")
                    }
                }) {
                    Text("⠿")
                }
            }
            Div {
                Div({ style { fontWeight("bold"); marginBottom(Spacing.xs) } }) {
                    Span({
                        style {
                            // T048: Strikethrough for deleted section names
                            if (isArchived) property("text-decoration", "line-through")
                            color(if (isArchived) Color(colors.textMuted) else Color(colors.text))
                        }
                    }) {
                        Text(section.internalName)
                    }
                    // T028: Show item count for carousel sections
                    if (section.sectionType == "carousel" || section.sectionType == "episode_carousel") {
                        val count = section.itemCount ?: 0
                        Span({ style { marginLeft(Spacing.sm); color(Color(colors.textMuted)); fontWeight("normal"); fontSize(Typography.base) } }) {
                            Text(if (count == 0) "(empty)" else "($count items)")
                        }
                    }
                }
                Div({ style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(Spacing.sm); fontSize(Typography.base); color(Color(colors.textMuted)) } }) {
                    Span({ style { fontWeight("700"); color(Color(colors.text)); fontSize(Typography.lg) } }) {
                        Text("#${section.displayOrder + 1}")
                    }
                    Span { Text("|") }
                    Span { Text(section.sectionType) }
                }
                // Section ID on its own line with copy button
                var idCopied by remember { mutableStateOf(false) }
                Div({ style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(Spacing.xs); marginTop(2.px) } }) {
                    Span({ style { fontFamily("monospace"); fontSize(Typography.sm); color(Color(colors.textMuted)) } }) {
                        Text(section.id)
                    }
                    Span({
                        onClick { evt ->
                            evt.stopPropagation()
                            kotlinx.browser.window.navigator.clipboard.writeText(section.id)
                            idCopied = true
                        }
                        style {
                            cursor("pointer")
                            fontSize(Typography.sm)
                            color(Color(if (idCopied) colors.success else colors.textMuted))
                            padding(2.px, 4.px)
                            borderRadius(BorderRadius.sm)
                            property("transition", "color 0.2s")
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                        }
                        if (!idCopied) {
                            onMouseOver { it.target.asDynamic().style.color = colors.primary }
                            onMouseOut { it.target.asDynamic().style.color = colors.textMuted }
                        }
                        attr("title", if (idCopied) "Copied!" else "Copy ID to clipboard")
                    }) {
                        if (idCopied) {
                            // Checkmark icon (SVG-like using borders)
                            com.castly.admin.ui.icons.IconCheck(size = 14)
                        } else {
                            // Copy icon
                            com.castly.admin.ui.icons.IconCopy(size = 14)
                        }
                    }
                }
                if (idCopied) {
                    LaunchedEffect(idCopied) {
                        kotlinx.coroutines.delay(1500)
                        idCopied = false
                    }
                }
                // T047: Display deletion date for deleted sections
                section.deletedAt?.let { deletedAt ->
                    Div({ style { fontSize(Typography.sm); color(Color(colors.danger)); marginTop(2.px) } }) {
                        Text("Deleted on ${formatDeletionDate(deletedAt)}")
                    }
                }
            }
        }

        // Actions
        Div({ style { display(DisplayStyle.Flex); property("gap", "4px"); alignItems(AlignItems.Center) } }) {
            if (isArchived) {
                IconButton(
                    icon = { IconRefreshCw() },
                    label = "Restore",
                    onClick = { onRestore() },
                    color = colors.success
                )
            } else {
                IconButton(
                    icon = { IconEdit() },
                    label = "Edit",
                    onClick = { onEdit() }
                )
                IconButton(
                    icon = { IconTrash() },
                    label = "Delete",
                    onClick = { onDelete() },
                    color = colors.danger
                )
            }
        }
    }
}
